/**
 * The time-of-day greeting on a dashboard.
 *
 * The barrister dashboard said "Good morning" at every hour — at nine in the evening it still
 * wished you good morning. The clerk dashboard had the correct logic but kept it to itself, so
 * the two screens disagreed about what time it was. One definition, used by both.
 *
 * Boundaries follow ordinary British usage: afternoon begins at noon, evening at six.
 *
 * IN THE PERSON'S TIMEZONE, not the device's. A barrister's working day is the one their court
 * sits in: signing in from abroad should not rewrite it. `effective.timezone` resolves their own
 * setting, or the practice's where they have not set one (lib/preferences.ts).
 *
 * The date shown beneath the greeting must be formatted in the SAME timezone, or the two
 * contradict each other — a device in Auckland at 09:00 on the 16th is 21:00 on the 15th in
 * London, so the greeting would read "Good evening" above a date reading Friday the 16th.
 */
export function greetingForHour(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * The hour (0–23) at `date` as read in `timeZone`.
 *
 * Falls back to the device clock if the timezone is unusable rather than throwing: a greeting is
 * decoration, and must never be the reason a dashboard fails to render.
 */
export function hourInTimeZone(date: Date, timeZone: string): number {
  try {
    const formatted = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: 'numeric',
      hour12: false,
    }).format(date);
    const hour = Number.parseInt(formatted, 10);
    // `hour12: false` yields "24" at midnight in some environments.
    return Number.isFinite(hour) ? hour % 24 : date.getHours();
  } catch {
    return date.getHours();
  }
}

export function getGreeting(timeZone?: string | null, now: Date = new Date()): string {
  return greetingForHour(timeZone ? hourInTimeZone(now, timeZone) : now.getHours());
}
